import React, {useState} from 'react';
import {Box, Typography, Button, Divider} from '@mui/material';
import EditIcon from '$assets/svgs/edit-icon-button.svg?react';
import DeleteIcon from '$assets/svgs/deleteicon.svg?react';
import {formatCurrency} from '$modules/frontdesk/utils';
import {
  detailsRowStyles,
  detailsLabelStyles,
  detailsValueStyles,
  descriptionValueStyles,
  editButtonStyles,
  detailsListStyles,
  sectionTitleStyles,
  actionButtonStyles,
} from './styles';
import DialogComponent from '$components/DialogComponent';
import FrontdeskButton from '$components/FrontdeskButton';
import {TextField} from '@mui/material';
import {RefundDetails} from './types';
import {useDeletePaymentItem} from './hooks';

interface PaymentItem {
  id: number;
  description: string;
  notes: string | null;
  ccsd_code: string | null;
  amount: number;
  category: string;
  item_name: string;
  refund_details?: RefundDetails;
}

interface PaymentItemsListProps {
  items: PaymentItem[];
  isFinanceRole?: boolean;
  onEditItemDescription?: (itemId: number, description: string) => void;
  isUpdating?: boolean;
  refundDetails?: RefundDetails;
}

const PaymentItemsList: React.FC<PaymentItemsListProps> = ({
  items,
  isFinanceRole = false,
  onEditItemDescription,
  isUpdating = false,
  refundDetails,
}) => {
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [editedDescription, setEditedDescription] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);

  const {deletePaymentItem, isPending: isDeleting} = useDeletePaymentItem();

  const handleEditClick = (item: PaymentItem) => {
    setEditingItemId(item.id);
    setEditedDescription(item.description || '');
  };

  const handleSave = () => {
    if (editingItemId && onEditItemDescription) {
      onEditItemDescription(editingItemId, editedDescription);
      setEditingItemId(null);
    }
  };

  const handleDeleteClick = (item: PaymentItem) => {
    setItemToDelete(item.id);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (itemToDelete) {
      deletePaymentItem(itemToDelete.toString());
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
    }
  };

  const getDisplayLabel = (item: PaymentItem) => {
    switch (item.category) {
      case 'Consultation':
        return 'Consultant Name';
      default:
        return 'Procedure Name';
    }
  };

  return (
    <Box>
      <Box sx={detailsListStyles}>
        <Typography sx={sectionTitleStyles}>Payment details</Typography>
        {items.length > 0 ? (
          items.map((item, itemIndex) => (
            <React.Fragment key={item.id}>
              <Box sx={detailsRowStyles}>
                <Typography sx={{...detailsLabelStyles, minWidth: '120px'}}>
                  {getDisplayLabel(item)}
                </Typography>
                <Typography sx={detailsValueStyles}>
                  {item.item_name}
                </Typography>
              </Box>
              <Box sx={detailsRowStyles}>
                <Typography sx={{...detailsLabelStyles, minWidth: '120px'}}>
                  Amount
                </Typography>
                <Typography sx={detailsValueStyles}>
                  {formatCurrency(item.amount)}
                </Typography>
              </Box>
              {refundDetails && (
                <Box sx={detailsRowStyles}>
                  <Typography sx={{...detailsLabelStyles, minWidth: '120px'}}>
                    Refund
                  </Typography>
                  <Typography sx={detailsValueStyles}>
                    {'+' +
                      formatCurrency(
                        refundDetails?.amount ? Number(refundDetails.amount) : 0
                      )}
                  </Typography>
                </Box>
              )}
              <Box sx={detailsRowStyles}>
                <Box sx={{display: 'flex', flex: 1}}>
                  <Typography sx={detailsLabelStyles}>Description</Typography>
                  <Typography sx={descriptionValueStyles}>
                    {item.description || '-'}
                  </Typography>
                </Box>
                {isFinanceRole && (
                  <Box sx={actionButtonStyles}>
                    <Button
                      onClick={() => handleEditClick(item)}
                      sx={editButtonStyles}
                    >
                      <EditIcon />
                    </Button>
                    <Button
                      onClick={() => handleDeleteClick(item)}
                      sx={editButtonStyles}
                    >
                      <DeleteIcon />
                    </Button>
                  </Box>
                )}
              </Box>
              {itemIndex < items.length - 1 && <Divider sx={{my: 2}} />}
            </React.Fragment>
          ))
        ) : (
          <Typography sx={{color: 'text.secondary', mt: 2}}>
            No payment items found
          </Typography>
        )}
      </Box>

      <DialogComponent
        open={!!editingItemId}
        handleClose={() => setEditingItemId(null)}
        dialogTitle={`Edit Item Description ${editingItemId ? `(ID: ${editingItemId})` : ''}`}
        dialogBody={
          <Box sx={{p: 3}}>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={editedDescription}
              onChange={e => setEditedDescription(e.target.value)}
              sx={{mb: 2}}
            />
            <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 2}}>
              <FrontdeskButton
                title="Cancel"
                variant="danger"
                onClick={() => setEditingItemId(null)}
                disabled={isUpdating}
              />
              <FrontdeskButton
                title="Save"
                onClick={handleSave}
                disabled={isUpdating}
              />
            </Box>
          </Box>
        }
      />

      <DialogComponent
        open={deleteConfirmOpen}
        handleClose={() => {
          setDeleteConfirmOpen(false);
          setItemToDelete(null);
        }}
        dialogTitle="Delete Payment Item"
        dialogBody={
          <Box sx={{p: 3}}>
            <Typography>
              Are you sure you want to delete this payment item?
            </Typography>
            <Box
              sx={{display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3}}
            >
              <FrontdeskButton
                title="Cancel"
                variant="danger"
                onClick={() => {
                  setDeleteConfirmOpen(false);
                  setItemToDelete(null);
                }}
                disabled={isDeleting}
              />
              <FrontdeskButton
                title="Delete"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              />
            </Box>
          </Box>
        }
      />
    </Box>
  );
};

export default PaymentItemsList;
